import * as Yup from "yup";
import i18next from "../../i18n";

/**
 * One address row managed by `AddressesForm`'s `useFieldArray`. Mirrors
 * `CompanyAddressResponse` (`types/company.type.ts`) field-for-field — `id`/`country` stay
 * optional/absent on a freshly-added row, exactly like the pre-Carbon form's own `newAddress`
 * seed object.
 */
export interface AddressFormFields {
  id?: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  countryId: string;
  isLegalAddress: boolean;
  isDeliverySite: boolean;
}

export interface AddressesFormFields {
  addresses: AddressFormFields[];
}

const maxLengthMessage = (max: number) =>
  i18next.t("validations.maxLength", {
    max,
    defaultValue: `Must not exceed ${max} characters`,
  });

/**
 * `street`/`city`/`state`/`postalCode`/`countryId` are `.required()` — reproducing the pre-Carbon
 * form's own gate EXACTLY: its "Add Address" button only staged a new row at all when
 * `newAddress?.street && newAddress?.city && newAddress?.state && newAddress?.postalCode &&
 * newAddress?.countryId` were all truthy (see that form's own `onClick`). That gate is also WHY
 * requiring these fields here is safe for every row already persisted in the database, not just
 * new ones: the ONLY path that ever created a `CompanyAddress` row was through this exact gate, so
 * every existing address already satisfies it — there is no legacy row this rule could newly
 * block from being re-saved. `isLegalAddress`/`isDeliverySite` were always optional booleans
 * (default `false`) pre-Carbon, and stay that way — the backend's own `AddressRequest` DTO
 * defaults both to `false` too.
 *
 * Max-lengths are new, mirroring `CompanyProfileUpdateRequest.AddressRequest`'s bean-validation
 * `@Size` constraints exactly (`street` 500, `city`/`state` 255, `postalCode` 50) — same rationale
 * as `basicInformationValidationSchema.ts`'s own max-length additions: catches a payload the
 * backend would 400 on, before it's sent.
 */
export const addressRowValidationSchema = Yup.object({
  id: Yup.string().optional(),
  street: Yup.string()
    .required(i18next.t("validations.street"))
    .max(500, maxLengthMessage(500)),
  city: Yup.string()
    .required(i18next.t("validations.city"))
    .max(255, maxLengthMessage(255)),
  state: Yup.string()
    .required(i18next.t("validations.state"))
    .max(255, maxLengthMessage(255)),
  postalCode: Yup.string()
    .required(i18next.t("validations.postalCode"))
    .max(50, maxLengthMessage(50)),
  countryId: Yup.string().required(i18next.t("validations.country")),
  isLegalAddress: Yup.boolean().default(false),
  isDeliverySite: Yup.boolean().default(false),
});

/**
 * The pre-Carbon form never validated the LIST itself (zero addresses was, and stays, a valid
 * state — an empty company address book) — only each staged row, at the moment it was added.
 * `.min()`/`.required()` on the array itself would be a new restriction with no pre-Carbon or
 * backend counterpart, so none is added here.
 */
export const addressesValidationSchema = Yup.object({
  addresses: Yup.array().of(addressRowValidationSchema).default([]),
});
